/**
 * SIS GradeLevels enum ids → display names, used as fallback labels when a student record
 * has no GradeLevelName. Same ids the NHA CMS resources API is keyed by.
 */
export const GRADE_LEVEL_NAMES: Record<number, string> = {
  403: 'Pre-K',
  399: 'Young 5s Kindergarten',
  1: 'Kindergarten',
  323: 'Full-Day Kindergarten',
  2: '1st Grade',
  3: '2nd Grade',
  4: '3rd Grade',
  5: '4th Grade',
  6: '5th Grade',
  7: '6th Grade',
  8: '7th Grade',
  9: '8th Grade',
  404: '9th Grade',
  405: '10th Grade',
  406: '11th Grade',
  407: '12th Grade',
  408: '13th Grade',
};

// Grade ids in school order (Pre-K → 13th) — the ids themselves aren't ordinal.
const GRADE_LEVEL_SEQUENCE = [403, 399, 1, 323, 2, 3, 4, 5, 6, 7, 8, 9, 404, 405, 406, 407, 408];

/** Sort ordinal for a grade id; unknown ids sort to the end. */
export function gradeLevelOrdinal(gradeLevelID: number): number {
  const index = GRADE_LEVEL_SEQUENCE.indexOf(gradeLevelID);
  return index === -1 ? GRADE_LEVEL_SEQUENCE.length : index;
}

// High-school grade ids (9th–13th).
const HIGH_SCHOOL_GRADE_IDS = new Set([404, 405, 406, 407, 408]);

/** Whether a grade id is a high-school grade (9th–13th). */
export function isHighSchoolGrade(gradeLevelID: number | undefined): boolean {
  return gradeLevelID !== undefined && HIGH_SCHOOL_GRADE_IDS.has(gradeLevelID);
}

/** A grade-level choice for the resources dialog. */
export interface GradeLevelOption {
  id: number;
  name: string;
}

/** Display label for a grade id, preferring the SIS-provided name. */
export function gradeLevelLabel(gradeLevelID: number, gradeLevelName?: string): string {
  return gradeLevelName || GRADE_LEVEL_NAMES[gradeLevelID] || `Grade ${gradeLevelID}`;
}

/**
 * Distinct grade levels among the given students' records at a school (matched via the
 * SIS SchoolEID), for the school resources dialog's grade dropdown (CMS resources are
 * authored per school + grade). Current-year records preferred, falling back to any
 * record at the school; empty input → empty result (the dialog then shows the school's
 * superset).
 */
export function gradeOptionsForSchool(
  students: { StudentInSchoolRecords?: { SchoolID: number; GradeLevelID: number; GradeLevelName?: string; IsCurrentYear?: boolean }[] }[],
  schoolEID: number | undefined
): GradeLevelOption[] {
  const currentYear = new Map<number, GradeLevelOption>();
  const anyYear = new Map<number, GradeLevelOption>();

  for (const student of students) {
    for (const record of student.StudentInSchoolRecords ?? []) {
      if (record.SchoolID !== schoolEID || !record.GradeLevelID) continue;
      const option = { id: record.GradeLevelID, name: gradeLevelLabel(record.GradeLevelID, record.GradeLevelName) };
      anyYear.set(option.id, option);
      if (record.IsCurrentYear) {
        currentYear.set(option.id, option);
      }
    }
  }

  return [...(currentYear.size > 0 ? currentYear : anyYear).values()]
    .sort((a, b) => gradeLevelOrdinal(a.id) - gradeLevelOrdinal(b.id));
}
